import React from 'react';
import { useEffect, useState, useRef } from 'react';
import { Box, Button } from "@mui/material";
import PropTypes from 'prop-types';
import SaveIcon from '@mui/icons-material/Save';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import { GridToolbarContainer, GridActionsCellItem, } from '@mui/x-data-grid';
import { randomId } from '@mui/x-data-grid-generator';
import { DataGrid, GridToolbar } from "@mui/x-data-grid";
import { tokens } from "../../theme";
import Header from "../../components/Header";
import { useTheme } from "@mui/material";
import useAxiosPrivate from "../../hooks/useAxiosPrivate"
import ConfirmDialog from '../../components/ConfirmDialog';
import { useNavigate, useLocation } from 'react-router-dom';
import saveBucketTransactions from '../../components/managers/saveBucketTransactions';
import { useHideMode } from '../../theme';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import handleError from "../../hooks/handleError";



function EditToolbar(props) {
    const { setBucketTrans, setDisplayedBucketTrans } = props;

    const handleClick = () => {
        const id = randomId();
        const newRow = { id, name: '', actions: "INSERT" };
        setBucketTrans((oldRows) => [...oldRows, newRow])
        setDisplayedBucketTrans((oldRows) => [...oldRows, newRow]);
    };

    return (
        <GridToolbarContainer>
            <Button color="primary" startIcon={<AddIcon />} onClick={handleClick}>
                ADD TRANSACTION
            </Button>
        </GridToolbarContainer>
    );
}



EditToolbar.propTypes = {
    setDisplayedBucketTrans: PropTypes.func.isRequired,
};



const BucketTransactions = () => {

    useEffect(() => {
        document.title = 'Bucket Transactions | Fretless Finance';
    }, []);

    const theme = useTheme();
    const colors = tokens(theme.palette.mode);
    const hideTheme = useHideMode();
    const axiosPrivate = useAxiosPrivate();
    const navigate = useNavigate();
    const location = useLocation();
    var [bucketTransactions, setBucketTrans] = useState([]);
    var [DisplayedBucketTrans, setDisplayedBucketTrans] = useState([]);
    var [open, setOpen] = useState(false);
    var [deleteId, setDeleteId] = useState(0);
    const effectRan = useRef(false);
    var isMounted = useRef(false);
    const controller = new AbortController();
    const [pageSize, setPageSize] = useState(50);
    const [nextOffset, setNextOffset] = useState(0);
    const [sortModel, setSortModel] = React.useState([
        {
            field: 'transaction_date',
            sort: 'desc',
        },
    ]);
    var pageNum = 1;


    useEffect(() => {
        isMounted.current = true;
        var success = false;
        if (!effectRan.current) {
            const getBucketTransactions = async () => {
                try {
                    const BucketTransResponse = await axiosPrivate.get(`/bucket-transactions?limit=${pageSize}&offset=${nextOffset}&reverseSort=true`, {
                        signal: controller.signal
                    });
                    if (isMounted.current) {
                        const newBucketTransactions = BucketTransResponse.data;
                        setBucketTrans((bucketTransactions) => [...bucketTransactions, ...newBucketTransactions])
                        setDisplayedBucketTrans((DisplayedBucketTrans) => [...DisplayedBucketTrans, ...newBucketTransactions]);

                        var updatedNextOffset = nextOffset + pageSize;
                        setNextOffset(updatedNextOffset);
                        loadMore(updatedNextOffset);
                        success = true
                    }
                } catch (err) {
                    if ((err.response?.status === 401) || (err.response?.status === 403)) {
                        navigate('/sign-in', { state: { from: location }, replace: true });
                    }
                    else {
                        handleError(err);
                    }
                }
            };

            getBucketTransactions();
            effectRan.current = true;
        }
        return () => {
            if (success) {
                isMounted.current = false;
                controller.abort();
            }
        }
    }, []);


    const loadMore = (offset = nextOffset) => {
        isMounted.current = true;

        const getBucketTransactions = async () => {
            try {
                if (isMounted.current) {
                    const BucketTransResponse = await axiosPrivate.get(`/bucket-transactions?limit=${pageSize}&offset=${offset}&reverseSort=true`, {
                        signal: controller.signal
                    });
                    const newBucketTransactions = BucketTransResponse.data;
                    setBucketTrans((transactions) => [...transactions, ...newBucketTransactions])
                    setDisplayedBucketTrans((DisplayedBucketTrans) => [...DisplayedBucketTrans, ...newBucketTransactions]);
                    setNextOffset(nextOffset => nextOffset + pageSize);
                }
            } catch (err) {
                if ((err.response?.status === 401) || (err.response?.status === 403)) {
                    navigate('/sign-in', { state: { from: location }, replace: true });
                }
                else {
                    handleError(err);
                }
            }
        };

        getBucketTransactions();
    }


    const processRowUpdate = (newRow) => {
        const updatedRow = { ...newRow, actions: ((newRow.actions !== "INSERT" & newRow.actions !== "DELETE") ? "UPDATE" : newRow.actions) };
        setBucketTrans(bucketTransactions.map((row) => (row.id === updatedRow.id ? updatedRow : row)));
        setDisplayedBucketTrans(DisplayedBucketTrans.map((row) => ((row.id === updatedRow.id & updatedRow.actions !== "DELETE") ? updatedRow : row)));
        return updatedRow;
    };

    const handlePageSizeChange = (newPageSize) => {
        setPageSize(newPageSize)
    }

    const handlePageChange = (page) => {
        pageNum = page + 1;
        const endIndex = pageNum * pageSize;

        if (endIndex == DisplayedBucketTrans.length) {
            loadMore();
        }
    }

    const deleteBucketTrans = () => {
        const deletedRow = bucketTransactions.filter((row) => row.id === deleteId)[0]
        deletedRow.actions = "DELETE"

        processRowUpdate(deletedRow)
        setDisplayedBucketTrans(DisplayedBucketTrans.filter((row) => row.id !== deleteId));
    };

    const saveChangesHandler = event => {
        var updatedArray = bucketTransactions.filter((row) => row.actions !== "DELETE")
        updatedArray = updatedArray.map(row => ({ ...row, actions: null }));

        saveBucketTransactions(bucketTransactions);
        setBucketTrans(updatedArray)
    }

    const columns = [
        {
            field: 'actions',
            type: 'actions',
            headerName: 'Actions',
            width: 100,
            cellClassName: 'actions',
            getActions: ({ id }) => {
                return [
                    <GridActionsCellItem
                        icon={<DeleteIcon />}
                        label="Delete"
                        onClick={() => {
                            setDeleteId(id);
                            setOpen(true);
                        }}
                        color="inherit"
                    />,
                ];
            },
        },
        {
            field: "bucket",
            headerName: "Bucket",
            width: 200,
            type: "string",
            headerAlign: "center",
            align: "left",
            editable: true,
        },
        {
            field: "sub_bucket",
            headerName: "Sub Bucket",
            width: 200,
            type: "string",
            headerAlign: "center",
            align: "left",
            editable: true,
        },
        {
            field: "amount",
            headerName: "Amount",
            width: 100,
            type: "string",
            headerAlign: "center",
            align: "right",
            editable: true,
            valueFormatter: (params) => {
                const amount = params?.value?.toLocaleString('en-US', { style: 'currency', currency: 'USD' })
                return hideTheme.mode === "show" ? amount : '*'.repeat(9)
            }
        },
        {
            field: "transaction_date",
            headerName: "Date",
            width: 150,
            type: "string",
            headerAlign: "center",
            align: "center",
            editable: true,
        },
        {
            field: "trans_type",
            headerName: "Type",
            width: 100,
            type: "string",
            headerAlign: "center",
            align: "center",
            editable: true,
        },
        {
            field: "to_bucket",
            headerName: "To (Bucket)",
            width: 200,
            type: "string",
            headerAlign: "center",
            align: "left",
            editable: true,
        },
        {
            field: "to_sub_bucket",
            headerName: "To (Sub Bucket)",
            width: 200,
            type: "string",
            headerAlign: "center",
            align: "left",
            editable: true,
        },

        {
            field: "notes",
            headerName: "Notes",
            width: 800,
            type: "string",
            headerAlign: "center",
            align: "left",
            editable: true,
        },
    ];


    return (
        <Box m="20px">
            <Header
                title="BUCKET TRANSACTIONS"
                titleSize="h2"
                subtitle="Imaginary Transactions For Ya Money"
                subtitleSize="h5"
            />
            < ToastContainer />

            <Box
                m="40px 0 0 0"
                height="67vh"
                sx={{
                    "& .MuiDataGrid-root": {
                        border: "none",
                    },
                    "& .MuiDataGrid-cell": {
                        borderBottom: "none",
                    },
                    "& .name-column--cell": {
                        color: colors.greenAccent[300],
                    },
                    "& .MuiDataGrid-columnHeaders": {
                        backgroundColor: colors.blueAccent[400],
                        borderBottom: "none",
                    },
                    "& .MuiDataGrid-virtualScroller": {
                        backgroundColor: colors.primary[400],
                    },
                    "& .MuiDataGrid-footerContainer": {
                        borderTop: "none",
                        backgroundColor: colors.blueAccent[400],
                    },
                    "& .MuiCheckbox-root": {
                        color: `${colors.greenAccent[200]} !important`,
                    },
                    "& .MuiDataGrid-toolbarContainer .MuiButton-text": {
                        color: `${colors.grey[100]} !important`,
                    },
                }}
            >
                <DataGrid
                    rows={DisplayedBucketTrans}
                    columns={columns}
                    sortModel={sortModel}
                    onSortModelChange={(model) => setSortModel(model)}
                    pagination
                    pageSize={pageSize}
                    onPageSizeChange={(newPageSize) => handlePageSizeChange(newPageSize)}
                    rowsPerPageOptions={[50, 100, 200]}
                    // paginationMode="server"
                    onPageChange={(newPage) => handlePageChange(newPage)}
                    CanUserAddRows="True"
                    CanUserDeleteRows="True"
                    processRowUpdate={processRowUpdate}
                    components={{ Toolbar: EditToolbar }}
                    componentsProps={{
                        toolbar: { bucketTransactions: bucketTransactions, setBucketTrans: setBucketTrans, setDisplayedBucketTrans: setDisplayedBucketTrans }
                    }}
                    experimentalFeatures={{ newEditingApi: true }}
                    options={{ customToolbar: () => { } }}
                />
                <ConfirmDialog
                    title="Delete Bucket"
                    message="Are you sure you want to delete this?"
                    confirmBtnText="DELETE"
                    open={open}
                    setOpen={setOpen}
                    onConfirm={deleteBucketTrans}>
                </ConfirmDialog>
            </Box>
            <Box padding="1rem" align="right">
                <Button onClick={saveChangesHandler}
                    sx={{
                        backgroundColor: colors.greenAccent[500],
                        color: "#ffffff",
                        '&:hover': {
                            backgroundColor: colors.greenAccent[300],
                            color: "#ffffff",
                        },
                        fontSize: "14px",
                        fontWeight: "bold",
                        padding: "10px 20px",
                    }}
                >
                    <SaveIcon sx={{ mr: "10px" }} />
                    Save Changes
                </Button>
            </Box>

        </Box >
    );
};

export default BucketTransactions;